import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Shield, Loader2, AlertCircle, LogIn, Lock, ArrowLeft } from 'lucide-react'
import { useAuth } from '../context/AuthProvider'
import { signin } from '../api/client'

/** Set the oauth2_return cookie so the mms-service OAuth success handler redirects back here. */
function setOAuthReturnCookie(target: string) {
  const maxAge = 5 * 60 // 5 minutes
  document.cookie = `oauth2_return=${encodeURIComponent(target)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`
}

export default function SignIn() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, loading: authLoading, refreshMe } = useAuth()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/dashboard'

  useEffect(() => {
    if (authLoading || !user) return
    navigate(from === '/signin' ? '/dashboard' : from, { replace: true })
  }, [user, authLoading, from, navigate])

  useEffect(() => {
    if (!error) return
    const t = setTimeout(() => setError(null), 5000)
    return () => clearTimeout(t)
  }, [error])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!username || !password) { setError('Username and password are required.'); return }
    if (needsTwoFactor && !totpCode) { setError('Enter your 6-digit authentication code.'); return }
    setSubmitting(true)
    try {
      await signin({ username, password, totpCode: needsTwoFactor ? totpCode : undefined })
      await refreshMe()
      navigate(from === '/signin' ? '/dashboard' : from, { replace: true })
    } catch (err) {
      const e = err as { message?: string; data?: { details?: string } }
      if ((e?.data?.details || '').includes('TWO_FACTOR_REQUIRED')) {
        setNeedsTwoFactor(true)
        setError(null)
      } else {
        setError(e?.message || 'Sign in failed')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleOAuth = (provider: 'google' | 'apple') => {
    setOAuthReturnCookie(`${window.location.origin}/dashboard`)
    // mms-service is the OAuth host. In dev, Vite's proxy rewrites
    // /oauth2/* to mmsTarget; in production (Cloudflare Pages) we have to
    // hit the absolute mms-service URL because there is no proxy. Falls
    // back to relative when VITE_MMS_URL is unset (local `npm run dev`).
    const mmsBase = (import.meta.env.VITE_MMS_URL || '').replace(/\/+$/, '')
    window.location.href = `${mmsBase}/oauth2/authorization/${provider}`
  }

  if (authLoading || user) {
    return <div className="flex justify-center py-24"><Loader2 size={32} className="animate-spin text-brand-500" /></div>
  }

  return (
    <div className="relative min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-1/2 -top-24 -translate-x-1/2 h-96 w-[40rem] bg-brand-500/10 blur-3xl rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Explicit escape hatch back to the marketing page. The brand logo in the
            nav also routes to `/`, but an inline "Back to home" link above the
            card makes the cancel action obvious on a focused auth screen. */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 mb-4 text-xs font-medium text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-400 transition-colors">
          <ArrowLeft size={13} /> Back to home
        </Link>
        <div className="card p-8 md:p-10 space-y-6">
          <div className="text-center space-y-3">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 ring-4 ring-brand-500/10 flex items-center justify-center shadow-lg">
              <Shield size={28} className="text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
                Welcome back
              </h1>
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                Sign in to continue to IDFY
              </p>
            </div>
          </div>

          {error && (
            <div role="alert" className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="label">Username or email</label>
              <input id="username" type="text" autoComplete="username" value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={needsTwoFactor}
                // autoFocus so returning users can start typing immediately. Gated on
                // !needsTwoFactor because the TOTP input below already claims autoFocus
                // when the 2FA step is shown, and two autoFocus attributes on the same
                // page would race.
                autoFocus={!needsTwoFactor}
                className="input" required />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="label mb-0">Password</label>
                <Link to="/forgot-password" className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400">
                  Forgot password?
                </Link>
              </div>
              <input id="password" type="password" autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={needsTwoFactor}
                className="input" required />
            </div>

            {needsTwoFactor && (
              <div className="space-y-1.5">
                <label htmlFor="totp" className="label flex items-center gap-1.5">
                  <Lock size={13} /> Authentication code
                </label>
                <input id="totp" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  autoFocus
                  className="input tracking-[0.25em] text-center font-mono" />
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  From your authenticator app. Use a recovery code if you lost access.
                </p>
              </div>
            )}

            <button type="submit" disabled={submitting}
              className="btn-primary w-full justify-center">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
              {needsTwoFactor ? 'Verify & continue' : 'Sign in'}
            </button>
          </form>

          <div className="relative flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            <span className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500">or continue with</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => handleOAuth('google')}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700/60 transition-colors">
              <GoogleGlyph /> Google
            </button>
            <button type="button" onClick={() => handleOAuth('apple')}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700/60 transition-colors">
              <AppleGlyph /> Apple
            </button>
          </div>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            Don't have an account?{' '}
            <Link to="/signup" className="text-brand-600 hover:text-brand-700 dark:text-brand-400 font-medium">
              Sign up
            </Link>
          </p>
        </div>

        <p className="mt-6 text-center text-[11px] text-gray-400 dark:text-gray-500">
          By continuing you agree to IDFY's Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  )
}

function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  )
}

function AppleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  )
}
